import React, { useState, useEffect } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons, Entypo } from '~/components/core/Icons';
import MarketStackNavigator from './MarketStackNavigator';
import i18n from '~/i18n';
import DealsStackNavigator from './DealsStackNavigator';
import { scaleSize } from '~/constants/scale';
import { FONT_FAMILIES } from '~/constants/typography';
import { useAIStrategyEnabled, useAppSelector, useStyles } from '~/hooks';
import { Theme } from '~/theme';
import { global } from '~/constants/global';
import { FONT_SIZE_S, FONT_SIZE_XS } from '~/constants';
import { linkTo } from '~/navigation';
import paths from '~/navigation/paths';
import { favoritesDataSelector, pendingMessagesCountSelector } from '~/state/selectors';
import MoreStackNavigator from './MoreStackNavigator';
import NewsStackNavigator from './NewsStackNavigator';
import * as ScreenOrientation from 'expo-screen-orientation';
import { useTheme } from '~/hooks/useTheme';
import AIStrategyStackNavigator from './AIStrategyStackNavigator';

const Tab = createBottomTabNavigator();

// react-navigation fixes the bar's content box at 49pt, which the scaled icon + label outgrow
// past ~420pt of screen width.
const TAB_SCALE_MAX_WIDTH = 400;

export default function HomeTabsNavigator() {
    const theme = useTheme();
    const insets = useSafeAreaInsets();
    const styles = useStyles(stylesGenerator, { hasBottomInset: insets.bottom > 0 });
    const favorites = useAppSelector(favoritesDataSelector);
    const pendingMessagesCount = useAppSelector(pendingMessagesCountSelector);
    const aiStrategyEnabled = useAIStrategyEnabled();

    const [orientation, setOrientation] = useState(3);
    useEffect(() => {
        lockOrientation();
    }, [orientation]);

    const lockOrientation = async () => {
        if (Platform.OS !== 'web') {
            await ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP);
            const o = await ScreenOrientation.getOrientationAsync();
            setOrientation(o);
        }
    };

    return (
        <Tab.Navigator
            initialRouteName="MarketsStack"
            screenOptions={{
                tabBarLabelPosition: 'below-icon',
                // System font scaling overflows the fixed bar height like an oversized screen does.
                tabBarAllowFontScaling: false,
                tabBarActiveTintColor: theme.tabIconSelected,
                tabBarInactiveTintColor: theme.tabIconDefault,
                ...styles,
            }}
        >
            <Tab.Screen
                name="MarketsStack"
                component={MarketStackNavigator}
                listeners={{
                    tabPress: (e) => {
                        e.preventDefault();
                        linkTo(paths.watchlistItem(favorites.id));
                    },
                }}
                options={{
                    headerShown: false,
                    tabBarLabel: i18n.t('bottom-tab-markets'),
                    tabBarIcon: ({ color }) => (
                        <Ionicons name="globe-outline" color={color} size={scaleSize(24, TAB_SCALE_MAX_WIDTH)} />
                    ),
                }}
            />
            <Tab.Screen
                name="DealsStack"
                component={DealsStackNavigator}
                listeners={{
                    tabPress: (e) => {
                        e.preventDefault();
                        linkTo(paths.openTrades);
                    },
                }}
                options={{
                    headerShown: false,
                    tabBarLabel: i18n.t('bottom-tab-deals'),
                    tabBarIcon: ({ color }) => (
                        <Ionicons name="briefcase-outline" color={color} size={scaleSize(24, TAB_SCALE_MAX_WIDTH)} />
                    ),
                }}
            />
            {aiStrategyEnabled && (
                <Tab.Screen
                    name="AIStrategyStack"
                    component={AIStrategyStackNavigator}
                    listeners={({ navigation }) => ({
                        tabPress: (e) => {
                            e.preventDefault();
                            const stack: any = navigation
                                .getState()
                                .routes.find((r: any) => r.name === 'AIStrategyStack')?.state;
                            // Pressing the tab normally returns to the list. Not while the create
                            // wizard is open: that would drop an unsaved draft without asking,
                            // and switching tabs is not a decision to discard one.
                            if (stack?.routes?.[stack.index]?.name === 'CreateAIStrategy') {
                                navigation.navigate('AIStrategyStack');
                                return;
                            }
                            navigation.navigate('AIStrategyStack', {
                                screen: 'AIStrategiesList',
                            });
                        },
                    })}
                    options={{
                        headerShown: false,
                        tabBarLabel: i18n.t('bottom-tab-ai-strategy'),
                        tabBarIcon: ({ color }) => (
                            <Ionicons name="sparkles-outline" color={color} size={scaleSize(24, TAB_SCALE_MAX_WIDTH)} />
                        ),
                    }}
                />
            )}
            <Tab.Screen
                name="NewsStack"
                component={NewsStackNavigator}
                listeners={{
                    tabPress: (e) => {
                        e.preventDefault();
                        linkTo(paths.news);
                    },
                }}
                options={{
                    headerShown: false,
                    tabBarLabel: i18n.t('bottom-tab-news'),
                    tabBarIcon: ({ color }) => (
                        <Ionicons name="newspaper-outline" color={color} size={scaleSize(24, TAB_SCALE_MAX_WIDTH)} />
                    ),
                    tabBarBadge: pendingMessagesCount
                        ? pendingMessagesCount > 9
                            ? '9+'
                            : pendingMessagesCount
                        : undefined,
                    tabBarBadgeStyle: {
                        fontSize: scaleSize(FONT_SIZE_XS, TAB_SCALE_MAX_WIDTH),
                        backgroundColor: theme.tabIconSelected,
                        color: theme.white,
                    },
                }}
            />
            <Tab.Screen
                name="MoreStack"
                component={MoreStackNavigator}
                listeners={{
                    tabPress: (e) => {
                        e.preventDefault();
                        linkTo(paths.more);
                    },
                }}
                options={{
                    headerShown: false,

                    tabBarLabel: i18n.t('bottom-tab-more'),
                    tabBarIcon: ({ color }) => (
                        <Entypo name="dots-three-horizontal" color={color} size={scaleSize(24, TAB_SCALE_MAX_WIDTH)} />
                    ),
                }}
            />
        </Tab.Navigator>
    );
}

const stylesGenerator = (theme: Theme, custom?: { hasBottomInset: boolean }) =>
    StyleSheet.create({
        tabBarBadgeStyle: {
            fontSize: scaleSize(FONT_SIZE_XS, TAB_SCALE_MAX_WIDTH),
            top: scaleSize(15, TAB_SCALE_MAX_WIDTH),
            height: scaleSize(15, TAB_SCALE_MAX_WIDTH),
            minWidth: scaleSize(15, TAB_SCALE_MAX_WIDTH),
            lineHeight: scaleSize(15, TAB_SCALE_MAX_WIDTH),
            color: theme.white,
            backgroundColor: theme.tabIconSelected,
            fontFamily: FONT_FAMILIES.LATO_BOLD,
        },
        tabBarLabelStyle: {
            fontFamily: FONT_FAMILIES.LATO_BOLD,
            fontWeight: Platform.OS === 'web' ? 'bold' : 'regular',
            fontSize: scaleSize(FONT_SIZE_S, TAB_SCALE_MAX_WIDTH),
        },
        tabBarStyle: {
            minHeight: scaleSize(global.HOME_TABS_HEIGHT),
            alignItems: 'stretch',
        },
        tabBarIconStyle: {
            justifyContent: 'center',
        },
        // Where the inset already pads the bar, a second gap would overflow the 49pt box.
        tabBarItemStyle: {
            paddingTop: scaleSize(4, TAB_SCALE_MAX_WIDTH),
            paddingBottom: custom?.hasBottomInset ? 0 : scaleSize(6, TAB_SCALE_MAX_WIDTH),
        },
    });
